import { useEffect } from 'react';
import { getUserState } from '@carvana/core-auth';
import { GetExperiment } from '@carvana/core-experiment';
import { Experiments } from '@constants/index';
import { SarStore, setCoverageScreenType } from '@store/sar-store';
import { snapshot } from 'valtio/vanilla';
import { useUserSelections } from '../../../providers/user-selections';
import { CoverageScreenType, getType } from '../get-logic/coverage-type';
import { SAR_DATA, Warranty, Category, SubCategory, SubSubCategory } from '../../../constants';
import { getTitle } from '../get-logic/title';
import { getDescription } from '../get-logic/description';
import { getButtonTextAndLink } from '../get-logic/button-text-link';
import { getOutboundLink } from '../get-logic/outbound-link';
import { showDeductible } from '../get-logic/deductible';
import { showPreferredPartnersForm } from '../get-logic/show-preferred-partners-form';

const getDataSubset = (userSelections: any) => {
  const { carvanaCareWarranty } = snapshot(SarStore);
  let dataSubset: any = SAR_DATA(carvanaCareWarranty);
  if (userSelections.category) {
    dataSubset = dataSubset?.categories?.find(({ title }) => title === userSelections.category);
  }
  if (userSelections.subcategory) {
    dataSubset = dataSubset?.categories?.find(({ title }) => title === userSelections.subcategory);
  }
  if (userSelections.subsubcategory) {
    dataSubset = dataSubset?.categories?.find(({ title }) => title === userSelections.subsubcategory);
  }
  return dataSubset;
};

export type UserCoverageAndSelections = {
  warranty: Warranty;
  category?: Category;
  subcategory?: SubCategory;
  subsubcategory?: SubSubCategory;
  reportedConcernCount: number;
  glassConcernCount: number;
  mileage?: number;
  saleEffectiveDate?: string;
};

const getCarvanaCare = (dataSubset) => {
  if (!dataSubset) return null;
  switch (dataSubset?.find((x) => x?.type?.includes?.('CarvanaCare'))?.type) {
    case Warranty.CarvanaCareCosmetic:
      return 'CarvanaCare® Cosmetic';
    case Warranty.CarvanaCareEssential:
      return 'CarvanaCare® Essential';
    case Warranty.CarvanaCarePlus:
      return 'CarvanaCare® Plus';
    case Warranty.CarvanaCarePremier:
      return 'CarvanaCare® Premier';
    default:
      return null;
  }
};

export const useCoverage = () => {
  const { userSelections, userFeedData } = useUserSelections();
  const dataSubset = getDataSubset(userSelections);
  const { coverage: productsFromApi, reportedConcerns, make, mileage: saleMileage, saleEffectiveDate } = userFeedData;
  const productsWithinMileage = productsFromApi?.filter((x) => {
    if (
      (x.type === Warranty.CarvanaCareCosmetic || x.type === Warranty.CarvanaCarePremier) &&
      userSelections?.category === Category.Maintenance &&
      userSelections.subcategory === SubCategory.OilChangeAndOrTireRotation
    ) {
      return true;
    }
    if (x.type === Warranty.SevenDay || !x.totalCoverageInMiles) return true;

    const userEnteredMileage = userSelections?.mileage || saleMileage;
    const isOEMWarranty = x.type === Warranty.OEMBasic || x.type === Warranty.OEMPowerTrain;
    const maxMileageCovered = (isOEMWarranty ? 0 : saleMileage) + x.totalCoverageInMiles;
    return userEnteredMileage <= maxMileageCovered;
  });

  const matchAgainstProducts = (coverageName) => productsWithinMileage?.find((x) => x.type === coverageName);
  const matchingCoverage = dataSubset?.coverage?.find(matchAgainstProducts);
  const coverageData = productsWithinMileage?.find((x) => x.type === matchingCoverage);

  const cosmeticConcernCount = reportedConcerns?.filter((concern: any) =>
    concern.subCategory === SubCategory.Exterior || concern.subCategory === SubCategory.Interior
  ).length || 0;

  const glassConcernCount = reportedConcerns?.filter((concern: any) =>
    concern.subCategory === SubCategory.Glass
  ).length || 0;

  const userCoverageAndSelections: UserCoverageAndSelections = {
    warranty: matchingCoverage,
    reportedConcernCount: cosmeticConcernCount,
    glassConcernCount,
    saleEffectiveDate,
    ...userSelections,
  };

  const coverageScreenType = getType(userCoverageAndSelections);
  useEffect(() => {
    if (
      coverageScreenType !== CoverageScreenType.notCovered &&
      coverageScreenType !== CoverageScreenType.manufacturerCovered &&
      coverageScreenType !== CoverageScreenType.notCoveredCosmetic &&
      coverageScreenType !== CoverageScreenType.notCoveredGlass &&
      coverageScreenType !== CoverageScreenType.notCoveredAdditionalCosmetic &&
      coverageScreenType !== CoverageScreenType.notCoveredAdditionalKey &&
      coverageScreenType !== CoverageScreenType.notCoveredMissingAccessories &&
      coverageScreenType !== CoverageScreenType.cosmeticSubmitted &&
      coverageScreenType !== CoverageScreenType.contactUs
    ) {
      getINFRandONFRDeductibleExperiment();
    }

    setCoverageScreenType(coverageScreenType);
  }, [coverageScreenType, userFeedData]);

  return {
    determineCoverage: {
      coverageScreenType,
      title: getTitle(coverageScreenType, userCoverageAndSelections),
      description: getDescription(
        coverageScreenType,
        make,
        coverageData,
        userSelections,
        getCarvanaCare(productsFromApi)
      ),
      button: getButtonTextAndLink(coverageScreenType, make),
      deductible: showDeductible(coverageScreenType, coverageData?.deductible),
      outboundLinks: getOutboundLink(coverageScreenType),
      showPreferredPartnersForm: showPreferredPartnersForm(coverageScreenType),
    },
    warranties: dataSubset?.coverage as string[],
    userCoverageAndSelections,
    hasCoverage: coverageData?.daysLeft > 0,
    deductible: coverageData?.deductible,
    daysLeft: coverageData?.daysLeft as number | undefined,
    carvanaCare: getCarvanaCare(productsFromApi),
  };
};

const getINFRandONFRDeductibleExperiment = async () => {
  const experimentName = Experiments.INF_ONF_DEDUCTIBLE;
  const userId = getUserState()?.user?.user_id;
  const isLocal = process.env.LOCAL === 'true' || process.env.PLAYGROUND === 'true';

  const bucket = await GetExperiment({
    name: experimentName,
    identifier: userId,
    environment: process.env.NODE_ENV,
    skipBucketing: !isLocal && !userId, // Skip bucketing if userId is not available
  });

  return bucket;
};

export default useCoverage;
